import { useState, useEffect, useMemo } from "react";
import type { CoverageStatistics, CoverageStatisticsConfig } from "../types/coverageStatistics";
import { calculateStatisticsFromImageData } from "../utils/coverageStatistics";

interface UseCoverageStatisticsParams {
  /** Heatmap ImageData to analyze */
  imageData: ImageData | null;
  /** Configuration options */
  config?: Partial<CoverageStatisticsConfig>;
  /** Whether to calculate statistics */
  enabled?: boolean;
}

interface UseCoverageStatisticsResult {
  /** Calculated coverage statistics */
  statistics: CoverageStatistics | null;
  /** Whether statistics are being calculated */
  isCalculating: boolean;
  /** Error message if calculation failed */
  error: string | null;
}

/**
 * Hook to calculate coverage statistics from heatmap ImageData
 * Automatically recalculates when imageData changes
 */
export function useCoverageStatistics({
  imageData,
  config = {},
  enabled = true,
}: UseCoverageStatisticsParams): UseCoverageStatisticsResult {
  const [statistics, setStatistics] = useState<CoverageStatistics | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize config to prevent unnecessary recalculations
  const memoizedConfig = useMemo(() => config, [JSON.stringify(config)]);

  useEffect(() => {
    if (!enabled || !imageData) {
      setStatistics(null);
      setIsCalculating(false);
      return undefined;
    }

    setIsCalculating(true);
    setError(null);

    // Use requestAnimationFrame to avoid blocking UI
    const rafId = requestAnimationFrame(() => {
      try {
        const stats = calculateStatisticsFromImageData(imageData, memoizedConfig);
        setStatistics(stats);
        setIsCalculating(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to calculate statistics");
        setStatistics(null);
        setIsCalculating(false);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [imageData, memoizedConfig, enabled]);

  return {
    statistics,
    isCalculating,
    error,
  };
}

/**
 * Hook to track coverage statistics over time (for videos)
 */
interface UseCoverageEvolutionParams {
  /** Function to get heatmap at a specific time */
  getHeatmapAtTime: (time: number) => ImageData | null;
  /** Video duration in seconds */
  duration: number;
  /** Sampling interval in seconds (default: 1s) */
  sampleInterval?: number;
  /** Configuration options */
  config?: Partial<CoverageStatisticsConfig>;
  /** Whether to calculate */
  enabled?: boolean;
}

interface CoverageEvolutionPoint {
  time: number;
  coveragePercentage: number;
  entropy: number;
  giniCoefficient: number;
}

interface UseCoverageEvolutionResult {
  /** Array of coverage data points over time */
  evolution: CoverageEvolutionPoint[];
  /** Whether evolution is being calculated */
  isCalculating: boolean;
  /** Calculation progress (0-100) */
  progress: number;
  /** Error message if calculation failed */
  error: string | null;
}

export function useCoverageEvolution({
  getHeatmapAtTime,
  duration,
  sampleInterval = 1,
  config = {},
  enabled = true,
}: UseCoverageEvolutionParams): UseCoverageEvolutionResult {
  const [evolution, setEvolution] = useState<CoverageEvolutionPoint[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Memoize config
  const memoizedConfig = useMemo(() => config, [JSON.stringify(config)]);

  useEffect(() => {
    if (!enabled || duration <= 0) {
      setEvolution([]);
      setIsCalculating(false);
      setProgress(0);
      return;
    }

    setIsCalculating(true);
    setError(null);
    setProgress(0);

    const points: CoverageEvolutionPoint[] = [];
    const sampleTimes: number[] = [];

    // Generate sample times
    for (let t = 0; t <= duration; t += sampleInterval) {
      sampleTimes.push(t);
    }
    // Ensure we include the final time
    if (sampleTimes[sampleTimes.length - 1] < duration) {
      sampleTimes.push(duration);
    }

    let currentIndex = 0;

    const processNextSample = () => {
      if (currentIndex >= sampleTimes.length) {
        setEvolution(points);
        setIsCalculating(false);
        setProgress(100);
        return;
      }

      try {
        const time = sampleTimes[currentIndex];
        const heatmap = getHeatmapAtTime(time);

        if (heatmap) {
          const stats = calculateStatisticsFromImageData(heatmap, memoizedConfig);
          points.push({
            time,
            coveragePercentage: stats.coveragePercentage,
            entropy: stats.entropy,
            giniCoefficient: stats.giniCoefficient,
          });
        }

        currentIndex++;
        setProgress(Math.round((currentIndex / sampleTimes.length) * 100));

        // Process next sample in next frame
        requestAnimationFrame(processNextSample);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to calculate evolution");
        setIsCalculating(false);
      }
    };

    requestAnimationFrame(processNextSample);
  }, [getHeatmapAtTime, duration, sampleInterval, memoizedConfig, enabled]);

  return {
    evolution,
    isCalculating,
    progress,
    error,
  };
}
